import React, { useEffect, useMemo, useState } from 'react';
import { timelineAPI } from '../services/api';
import { ChevronDown, ChevronRight, AlertTriangle, Clock, User } from 'lucide-react';
import { useToast } from './Toast';

const AdminActivityFeed = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [critical, setCritical] = useState([]);
  const [routine, setRoutine] = useState([]);
  const [routineOpen, setRoutineOpen] = useState(false);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch (e) {
      return null;
    }
  }, []);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await timelineAPI.getAdminFeed({ limit: 200 });
      setCritical(res.data?.critical || []);
      setRoutine(res.data?.routine || []);
    } catch (err) {
      console.error('Failed to load admin feed:', err);
      showToast(err.response?.data?.error || 'Failed to load admin activity feed', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <p className="text-sm text-gray-700">Admin only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Admin Activity Feed</h1>
        <p className="text-sm text-gray-500 mt-1">Grouped by importance for fast review</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Critical</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-100">{critical.length}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
          </div>
        ) : critical.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No critical events yet</p>
        ) : (
          <div className="space-y-3">
            {critical.map((e) => (
              <div key={e.id} className="rounded-xl border border-gray-100 bg-gray-50/40 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{e.lead_name || `Lead #${e.lead_id}`}</p>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{e.content || e.event_type}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1.5"><Clock size={14} />{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
                      <span className="inline-flex items-center gap-1.5"><User size={14} />{e.actor_name || 'System'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <button
          type="button"
          onClick={() => setRoutineOpen((v) => !v)}
          className="w-full flex items-center justify-between px-6 py-4"
        >
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Routine</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">{routine.length}</span>
          </div>
          {routineOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        {routineOpen && (
          <div className="px-6 pb-6">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
              </div>
            ) : routine.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">No routine events yet</p>
            ) : (
              <div className="space-y-3">
                {routine.map((e) => (
                  <div key={e.id} className="rounded-xl border border-gray-100 bg-gray-50/40 p-4">
                    <p className="text-sm font-semibold text-gray-900">{e.lead_name || `Lead #${e.lead_id}`}</p>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{e.content || e.event_type}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1.5"><Clock size={14} />{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
                      <span className="inline-flex items-center gap-1.5"><User size={14} />{e.actor_name || 'System'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivityFeed;

